import { defineStore } from 'pinia';
import { useNextCardStore } from '@/store/nextcard';
import { useHotelsStore } from '@/store/hotels';

export const useTransferPartnersStore = defineStore('transferPartners', {
    state: () => ({
        allTransferPartners: [],
        auditEntries: [],
        bonuses: [],
        cardRecsByUser: [],
        currentPage: 1,
        direction: 'desc',
        error: null,
        lastUpdated: null,
        loading: false,
        partners: [],
        perPage: 25,
        selectedProgram: {},
        sort: 'bonus_end_date',
        totalItems: 0,
        totalPages: 1
    }),
    actions: {
        async getData(url) {
            const response = await ApiService.apiCall(`/${url}`, 'get');
            return response.data;
        },

        async getTransferPartnersForProgram(programId) {
            //gets transfer partners for DESTINATION program (e.g., which programs transfer to Hilton)
            const hotelsStore = useHotelsStore();
            hotelsStore.setLoading(true);
            try {
                const partners = await this.getData(
                    `transfer-partners/${programId}`
                );
                if (partners.partners.data) {
                    this.setPartners(partners.partners.data);
                } else {
                    this.setPartners([]);
                    throw new Error(
                        `No transfer partners for property with id ${programId} found!`
                    );
                }
            } catch (error) {
                console.error(error);
            } finally {
                hotelsStore.setLoading(false);
            }
        },

        async getTransferPartnerAudit(transferPartnerId) {
            try {
                const response = await this.getData(
                    `admin/audit?entity_type=transfer_partner&entity_id=${transferPartnerId}`
                );
                return response.audit || [];
            } catch (error) {
                this.setError(error);
                console.error('Error fetching transfer partner audit:', error);
                throw error;
            }
        },

        async getTransferPartnerMatchAudit(matchId) {
            try {
                const response = await this.getData(
                    `admin/audit?entity_type=transfer_partner_match&entity_id=${matchId}`
                );
                return response.audit || [];
            } catch (error) {
                this.setError(error);
                console.error('Error fetching transfer partner match audit:', error);
                throw error;
            }
        },

        async getTransferPartnerBonusAudit(bonusId) {
            try {
                const response = await this.getData(
                    `admin/audit?entity_type=transfer_partner_bonus&entity_id=${bonusId}`
                );
                return response.audit || [];
            } catch (error) {
                this.setError(error);
                console.error('Error fetching transfer partner bonus audit:', error);
                throw error;
            }
        },

        async getAllGroupTransferPartners() {
            try {
                const response = await this.getData(
                    'transfer-partners/type/hotel'
                );
                if (response.partners && response.partners.data) {
                    this.allTransferPartners = response.partners.data;
                } else {
                    throw new Error('No transfer partners found');
                }
            } catch (error) {
                this.setError(error);
                console.error(error);
                throw error;
            }
        },
        setError(err) {
            this.error = err;
        },
        setSelectedProgram(program) {
            this.selectedProgram = program;
        },

        setPartners(partnersArray) {
            this.partners = partnersArray;
        },

        getCardRecs() {
            const nextCardStore = useNextCardStore();
            // Find card recommendations and create an object with user ID as key
            if (nextCardStore.users.length) {
                const bestCardsByUser = nextCardStore.users.reduce(
                    (cardRecsByUser, currUser) => {
                        const topCards = currUser.next_cards.filter((card) => {
                            return !card.hidden;
                        });
                        cardRecsByUser[currUser.id] = {
                            userName: currUser.name,
                            topCards,
                        };
                        return cardRecsByUser;
                    },
                    {}
                );
                this.cardRecsByUser = bestCardsByUser;
            }
        },
        async fetchBonuses(page = 1, perPage = 25, sort = null, direction = null) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get('/admin/transfer-partner-bonuses/list', {
                    params: {
                        page: page || this.currentPage,
                        per_page: perPage || this.perPage,
                        sort: sort || this.sort,
                        direction: direction || this.direction
                    }
                });

                this.bonuses = response.data.bonuses;
                this.totalItems = response.data.pagination.total;
                this.totalPages = response.data.pagination.last_page;
                this.currentPage = response.data.pagination.current_page;
                this.perPage = response.data.pagination.per_page;
                this.lastUpdated = new Date();
            } catch (error) {
                this.error = 'Failed to load bonuses. Please try again.';
                console.error('Error fetching bonuses:', error);
            } finally {
                this.loading = false;
            }
        },
        async deleteBonus(id) {
            try {
                const response = await axios.delete(`/admin/transfer-partner-bonuses/${id}`);

                notify({
                    type: 'success',
                    text: response.data.message || 'Bonus deleted successfully'
                });

                await this.fetchBonuses();
                return true;
            } catch (error) {
                console.error('Error deleting bonus:', error);
                notify({
                    type: 'error',
                    text: 'Failed to delete bonus. Please try again.'
                });
                throw new Error('Failed to delete bonus');
            }
        },
        setSorting(column, direction) {
            this.sort = column;
            this.direction = direction;
        },

        setPage(page) {
            this.currentPage = page;
        },


    },
});
